/**
 * Объявление «сессии больше нет».
 *
 * ## Почему это отдельный модуль, а не ветка в `http.ts`
 *
 * `http.ts` намеренно не знает ни про роутер, ни про кэш, ни про экраны: он
 * транспорт. Но 401 — единственный отказ, который меняет состояние
 * ПРИЛОЖЕНИЯ, а не отдельного запроса: после него не бывает «неудачного
 * запроса на фоне работающего портала», бывает «портал больше не работает».
 * Кто-то обязан об этом узнать, и тащить React в транспорт ради этого нельзя.
 *
 * Устройство дословно повторяет `onRateLimitPause` из `api/queue.ts`: слой
 * объявляет факт, подписчик решает, что показать. Границы это не нарушает —
 * `http.ts` по-прежнему зависит только от соседей своего слоя.
 *
 * ## Защёлка, а не поток событий
 *
 * Первый же 401 гасит сессию до конца этой загрузки страницы. Экран ревизии
 * держит десяток запросов сразу, и все они получат 401 в одну секунду; без
 * защёлки подписчик получил бы десять уведомлений об одном событии, а
 * пользователь — десять перерисовок. Снимается защёлка входом, то есть
 * перезагрузкой страницы.
 *
 * ## Чего здесь нет
 *
 * Ни попытки продлить сессию, ни повтора запроса: продлевать нечем. Портал —
 * BFF с серверной сессией, в браузере нет ни access-, ни refresh-токена, а
 * скользящее окно двигает только успешный запрос. Пришедший 401 означает, что
 * сессии на сервере уже нет, и единственное лечение — вход.
 */

/** Защёлка: сессия потеряна до перезагрузки страницы. */
let lost = false;

const listeners = new Set<() => void>();

export function isSessionLost(): boolean {
  return lost;
}

/**
 * Подписка на конец сессии. Возвращает функцию отписки.
 *
 * Подписчик, пришедший ПОСЛЕ объявления, вызывается сразу: иначе гонка между
 * монтированием заслона и фоновым запросом решала бы, увидит ли пользователь
 * экран входа.
 */
export function onUnauthenticated(listener: () => void): () => void {
  listeners.add(listener);
  if (lost) listener();
  return () => {
    listeners.delete(listener);
  };
}

export function notifyUnauthenticated(): void {
  if (lost) return;
  lost = true;
  for (const listener of listeners) listener();
}

/** Сброс между тестами: защёлка живёт в модуле, а модуль переживает тест. */
export function resetSessionLostForTests(): void {
  lost = false;
  listeners.clear();
}
